import type { ReactNode } from 'react';
import { Sheet } from './Sheet';
import { Icon } from '@/components/ui/Icon';
import { cn } from '@/lib/cn';

export interface ActionSheetItem {
  id: string;
  label: string;
  /** Material Symbols glyph name. */
  icon: string;
  /** One-line subtitle under the label. */
  sub?: string;
  /** Render in pink (destructive / pink emphasis). */
  destructive?: boolean;
  onSelect: () => void;
}

interface ActionSheetProps {
  open: boolean;
  onClose: () => void;
  title?: ReactNode;
  eyebrow?: ReactNode;
  items: ActionSheetItem[];
  /** Optional inline message above the items (e.g. confirmation copy). */
  message?: ReactNode;
}

/**
 * Composed list-of-rows sheet. Wires Profile detail "more" button, Settings
 * Blocked profiles, etc. Each row closes the sheet on tap and fires onSelect.
 */
export const ActionSheet = ({
  open,
  onClose,
  title,
  eyebrow,
  items,
  message,
}: ActionSheetProps) => (
  <Sheet open={open} onClose={onClose} title={title} eyebrow={eyebrow}>
    {message && (
      <p className="mb-5 text-sm leading-relaxed text-text-secondary">{message}</p>
    )}
    <ul className="-mx-2 divide-y divide-white/[0.06]">
      {items.map((item) => (
        <li key={item.id}>
          <button
            type="button"
            onClick={() => {
              item.onSelect();
              onClose();
            }}
            className={cn(
              'group flex w-full items-center gap-4 px-2 py-4 text-left',
              'transition-colors duration-fast ease-signal',
              'hover:bg-white/[0.03]',
            )}
          >
            <span
              className={cn(
                'flex h-10 w-10 shrink-0 items-center justify-center border',
                item.destructive
                  ? 'border-pink/40 text-pink'
                  : 'border-white/[0.07] bg-charcoal/60 text-text-secondary',
              )}
            >
              <Icon name={item.icon} size={18} />
            </span>
            <div className="min-w-0 flex-1">
              <p
                className={cn(
                  'font-display text-sm font-bold uppercase tracking-wide',
                  item.destructive ? 'text-pink' : 'text-text-primary',
                )}
              >
                {item.label}
              </p>
              {item.sub && (
                <p className="mt-1 truncate text-xs text-text-secondary">
                  {item.sub}
                </p>
              )}
            </div>
            <Icon
              name="chevron_right"
              size={18}
              className="text-text-muted transition-colors duration-fast ease-signal group-hover:text-pink"
            />
          </button>
        </li>
      ))}
    </ul>
  </Sheet>
);
